import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Boxes, Loader2, AlertTriangle } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function StockForm({ item, info, onSave, onRemove, busy }) {
  const qty = Number(item.quantity || 0);
  const [val, setVal] = useState(String(item.stock_qty || Math.min(qty, info.on_hand) || ""));
  const n = Number(val) || 0;
  const over = n > info.available;
  return (
    <div className="space-y-2.5 text-xs" data-testid={`stock-form-${item.id}`}>
      <div className="font-semibold text-slate-800">Quote From Our Stock</div>
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded bg-slate-50 py-1.5"><div className="text-[10px] text-muted-foreground">On Hand</div><b>{fmt(info.on_hand)}</b></div>
        <div className="rounded bg-amber-50 py-1.5"><div className="text-[10px] text-muted-foreground">Held Elsewhere</div><b>{fmt(info.held_elsewhere)}</b></div>
        <div className="rounded bg-[#E4EEE8] py-1.5"><div className="text-[10px] text-muted-foreground">Available</div><b>{fmt(info.available)}</b></div>
      </div>
      {info.holds.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800" data-testid={`stock-holds-${item.id}`}>
          <div className="mb-1 flex items-center gap-1 font-semibold"><AlertTriangle className="h-3 w-3" />Already held for open quotes:</div>
          {info.holds.map((h, i) => <div key={i}>{h.rfq_code} · {h.client_name} — {fmt(h.qty)}</div>)}
        </div>
      )}
      <label className="block space-y-1">
        <span className="text-[11px] text-slate-600">Qty from stock (line needs {fmt(qty)})</span>
        <Input inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8" data-testid={`stock-qty-${item.id}`} />
      </label>
      {over && <p className="text-[11px] font-medium text-amber-700">This uses stock held for another open quote — you can still quote it.</p>}
      {n > 0 && n < qty && <p className="text-[11px] text-muted-foreground">The other {fmt(qty - n)} will be sourced from suppliers and blended into one quote line.</p>}
      <div className="flex justify-between gap-2">
        {item.stock_qty || item.line_type === "from_stock" ? <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={onRemove} disabled={busy} data-testid={`stock-remove-${item.id}`}>Stop Using Stock</Button> : <span />}
        <Button size="sm" className="h-8 bg-[#527D63] hover:bg-[#456a54]" disabled={busy || n <= 0 || n > info.on_hand} onClick={() => onSave(n)} data-testid={`stock-save-${item.id}`}>{busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}Use {fmt(n)} From Stock</Button>
      </div>
    </div>
  );
}

export function StockLineToggle({ item, onChanged }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const st = item.stock;
  const using = item.line_type === "from_stock" || Number(item.stock_qty || 0) > 0;
  if (!using && (!st || Number(st.on_hand || 0) <= 0)) return null;
  const openPop = async (v) => {
    setOpen(v);
    if (v) { setInfo(null); try { setInfo((await api.get(`/items/${item.id}/stock-holds`)).data); } catch (e) { toast.error(apiError(e)); setOpen(false); } }
  };
  const save = async (stock_qty) => {
    setBusy(true);
    try {
      await api.put(`/items/${item.id}/line-type`, stock_qty ? { line_type: "from_stock", stock_qty } : { line_type: "procure" });
      toast.success(stock_qty ? `${fmt(stock_qty)} will be quoted from stock` : "Line set back to supplier sourcing");
      setOpen(false); onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const label = item.line_type === "from_stock" ? `Somcore Stock · ${fmt(item.quantity)}` : using ? `Stock ${fmt(item.stock_qty)} / ${fmt(item.quantity)}` : `From Stock (${fmt(st.on_hand)})`;
  return (
    <Popover open={open} onOpenChange={openPop}>
      <PopoverTrigger asChild>
        <button className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${using ? "border-2 border-[#527D63] bg-[#E4EEE8] text-[#3F6B50]" : "border border-dashed border-[#527D63]/50 text-[#3F6B50] hover:bg-[#E4EEE8]"}`} data-testid={`item-stock-toggle-${item.id}`}>
          <Boxes className="h-3 w-3" />{label}{using && st ? <span className="font-normal opacity-75">· ${fmt(st.landed_cost_usd)}</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        {!info ? <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" /> : <StockForm item={item} info={info} busy={busy} onSave={save} onRemove={() => save(0)} />}
      </PopoverContent>
    </Popover>
  );
}
